/**
 * Shared enum vocabulary. These string unions mirror the Prisma enums in
 * apps/api/prisma/schema.prisma one-for-one — if you change one, change both.
 * Keeping them as plain unions lets the browser bundles import them without
 * pulling in the Prisma client.
 */
export const USER_TYPES = ['EMPLOYEE', 'CLIENT', 'ADMIN'];
export const ROLES = [
    'TELECALLER',
    'SALES_EXEC',
    'WA_EXEC',
    'SOCIAL_EXEC',
    'DESIGNER',
    'OPS_EXEC',
    'FINANCE',
    'HR_EXEC',
    'CS_EXEC',
    'TEAM_LEAD',
    'SUB_ADMIN',
    'SUPER_ADMIN',
    'CLIENT_OWNER',
    'CLIENT_MANAGER',
    'CLIENT_VIEWER',
];
/** Roles that may sign in to the admin portal. */
export const ADMIN_ROLES = ['SUB_ADMIN', 'SUPER_ADMIN'];
/** Roles that carry supervisory rights inside an organisation. */
export const LEAD_ROLES = ['TEAM_LEAD', 'SUB_ADMIN', 'SUPER_ADMIN'];
/** Everyone on staff. Client-portal roles are created from the Clients page. */
/**
 * The client's own logins. One business can have several — the owner, the
 * manager who runs the account, and whoever else only needs to read.
 */
export const CLIENT_ROLES = ['CLIENT_OWNER', 'CLIENT_MANAGER', 'CLIENT_VIEWER'];
export const STAFF_ROLES = ROLES.filter((r) => !CLIENT_ROLES.includes(r));
/**
 * What an admin may pick when creating an employee. SUPER_ADMIN is excluded on
 * purpose: the first one is made by prisma/create-admin.ts, and promoting
 * someone to unrestricted access should not be a dropdown option.
 */
export const ASSIGNABLE_ROLES = STAFF_ROLES.filter((r) => r !== 'SUPER_ADMIN');
/**
 * Staff who do daily client-facing delivery work — the roles that carry daily
 * targets and get assigned to organisations. Admin roles are excluded because
 * a target on someone who supervises rather than executes measures nothing.
 *
 * Derived rather than hand-listed: three separate screens used to keep their
 * own copy, so adding a role meant remembering all three.
 */
export const DELIVERY_ROLES = STAFF_ROLES.filter((r) => r !== 'SUB_ADMIN' && r !== 'SUPER_ADMIN');
export const DEPARTMENTS = [
    'SALES',
    'OPERATIONS',
    'FINANCE',
    'PEOPLE',
    'TECHNOLOGY',
    'PRODUCT',
    'CREATIVE',
];
export const PLANS = ['STARTER', 'GROWTH', 'SCALE', 'ENTERPRISE'];
export const ORG_STATUSES = ['ONBOARDING', 'ACTIVE', 'PAUSED', 'CHURNED'];
export const LEAD_STAGES = [
    'NEW',
    'CONTACTED',
    'QUALIFIED',
    'PROPOSAL',
    'NEGOTIATION',
    'WON',
    'LOST',
];
/** Terminal stages. Moving to LOST additionally requires a lostReason. */
export const TERMINAL_STAGES = ['WON', 'LOST'];
export const LEAD_SOURCES = [
    'WEBSITE',
    'REFERRAL',
    'WALK_IN',
    'IMPORT',
    'META_ADS',
    'GOOGLE_ADS',
    'WHATSAPP',
    'PHONE_IN',
    'OTHER',
];
export const TEMPERATURES = ['COLD', 'WARM', 'HOT'];
/** Preset loss reasons. Free text is also accepted. */
export const LOST_REASONS = [
    'PRICE',
    'TIMING',
    'COMPETITOR',
    'NO_REQUIREMENT',
    'UNREACHABLE',
    'NOT_A_FIT',
];
export const ACTIVITY_TYPES = [
    'CALL',
    'WHATSAPP',
    'EMAIL',
    'NOTE',
    'STAGE_CHANGE',
    'MEETING',
    'ASSIGNMENT',
    'INVOICE',
];
export const DIRECTIONS = ['OUTBOUND', 'INBOUND'];
export const CALL_STATUSES = ['RINGING', 'ANSWERED', 'NO_ANSWER', 'BUSY', 'FAILED'];
export const WA_STATUSES = ['OPEN', 'PENDING', 'RESOLVED'];
export const MSG_STATUSES = ['QUEUED', 'SENT', 'DELIVERED', 'READ', 'FAILED'];
export const INVOICE_STATUSES = ['DRAFT', 'SENT', 'PAID', 'OVERDUE', 'VOID'];
export const REPORT_STATUSES = ['DRAFT', 'SENT', 'FAILED'];
export const PLATFORMS = ['INSTAGRAM', 'FACEBOOK', 'LINKEDIN'];
export const POST_STATUSES = [
    'DRAFT',
    'PENDING_APPROVAL',
    'APPROVED',
    'SCHEDULED',
    'PUBLISHED',
    'REJECTED',
    'FAILED',
];
export const TASK_STATUSES = ['TODO', 'IN_PROGRESS', 'BLOCKED', 'DONE'];
export const PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'];
export const ALERT_SEVERITIES = ['INFO', 'WARNING', 'DANGER'];
export const LEAVE_TYPES = ['CASUAL', 'SICK', 'EARNED', 'UNPAID'];
export const APPROVAL_STATUSES = ['PENDING', 'APPROVED', 'REJECTED'];
export const PAYMENT_MODES = ['UPI', 'NEFT', 'CARD', 'CASH', 'CHEQUE', 'RAZORPAY'];
export const INTEGRATION_PROVIDERS = [
    'EXOTEL',
    'WHATSAPP_360',
    'RAZORPAY',
    'META',
    'LINKEDIN',
    'GOOGLE_CALENDAR',
    'GSTN',
];
/** GST slabs permitted on an invoice line item. */
export const TAX_RATES = [0, 5, 12, 18, 28];
/**
 * States and union territories, with their GST codes.
 *
 * Place of supply is not free text on a tax invoice — it decides whether the
 * tax splits into CGST+SGST or goes out as IGST, and a typo silently changes
 * what the client owes. One list, used by the picker and the validator.
 */
export const INDIAN_STATES = [
    { code: '01', name: 'Jammu and Kashmir' },
    { code: '02', name: 'Himachal Pradesh' },
    { code: '03', name: 'Punjab' },
    { code: '04', name: 'Chandigarh' },
    { code: '05', name: 'Uttarakhand' },
    { code: '06', name: 'Haryana' },
    { code: '07', name: 'Delhi' },
    { code: '08', name: 'Rajasthan' },
    { code: '09', name: 'Uttar Pradesh' },
    { code: '10', name: 'Bihar' },
    { code: '11', name: 'Sikkim' },
    { code: '12', name: 'Arunachal Pradesh' },
    { code: '13', name: 'Nagaland' },
    { code: '14', name: 'Manipur' },
    { code: '15', name: 'Mizoram' },
    { code: '16', name: 'Tripura' },
    { code: '17', name: 'Meghalaya' },
    { code: '18', name: 'Assam' },
    { code: '19', name: 'West Bengal' },
    { code: '20', name: 'Jharkhand' },
    { code: '21', name: 'Odisha' },
    { code: '22', name: 'Chhattisgarh' },
    { code: '23', name: 'Madhya Pradesh' },
    { code: '24', name: 'Gujarat' },
    { code: '26', name: 'Dadra and Nagar Haveli and Daman and Diu' },
    { code: '27', name: 'Maharashtra' },
    { code: '29', name: 'Karnataka' },
    { code: '30', name: 'Goa' },
    { code: '31', name: 'Lakshadweep' },
    { code: '32', name: 'Kerala' },
    { code: '33', name: 'Tamil Nadu' },
    { code: '34', name: 'Puducherry' },
    { code: '35', name: 'Andaman and Nicobar Islands' },
    { code: '36', name: 'Telangana' },
    { code: '37', name: 'Andhra Pradesh' },
    { code: '38', name: 'Ladakh' },
    { code: '97', name: 'Other Territory' },
];
export const INDIAN_STATE_NAMES = INDIAN_STATES.map((s) => s.name);
export function stateNameForCode(code) {
    return INDIAN_STATES.find((s) => s.code === code)?.name ?? null;
}
//# sourceMappingURL=enums.js.map